// ContactPopup — iter108.
//
// Dedicated "Contact [Name]" sheet used by every phone-circle button on list
// cards (My Places, My Favorites, My Saved, Friend Profile hotel cards, etc).
// Replaces the previous behaviour where the phone circle dispatched into the
// generic ride/business WhatsAppActionSheet — which felt like a ride request
// instead of a focused contact moment.
//
// The popup gives the user TWO clear actions:
//   • Send Message — opens https://wa.me/<phone>?text=<editable message>
//   • Call         — opens https://wa.me/<phone>  (no text param → triggers
//                    WhatsApp's native call/chat picker on the destination
//                    device). Hidden when the destination has no whatsapp.
//
// Singleton pattern: mounted once via <ContactPopupHost /> in App.js. Call
// sites fire the `habano:contact-open` CustomEvent (see
// `lib/sendRideRequest.js::openContactPopup`).
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Phone } from "lucide-react";
import { useI18n } from "../lib/i18n";

const EVENT = "habano:contact-open";

export function ContactPopupHost() {
  const [destination, setDestination] = useState(null);

  useEffect(() => {
    const handler = (e) => setDestination(e.detail?.destination || null);
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  if (!destination) return null;
  return createPortal(
    <ContactSheet destination={destination} onClose={() => setDestination(null)} />,
    document.body,
  );
}

function ContactSheet({ destination, onClose }) {
  const { t, lang } = useI18n();
  const defaultMsg = lang === "es"
    ? "Hola, me gustaría más información."
    : "Hi, I would like more information.";
  const [message, setMessage] = useState(defaultMsg);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Resolve the phone number — whatsapp preferred, then phone.
  const rawNumber = destination.whatsapp || destination.phone || "";
  const digits = rawNumber.replace(/[^\d]/g, "");
  const hasWhatsapp = !!destination.whatsapp;
  const canSend = digits.length > 0;

  const sendMessage = () => {
    if (!canSend) return;
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
    onClose();
    window.open(url, "_blank", "noopener");
  };

  const callViaWhatsapp = () => {
    if (!hasWhatsapp) return;
    const url = `https://wa.me/${digits}`;
    onClose();
    window.open(url, "_blank", "noopener");
  };

  const name = destination.name || "";
  const title = lang === "es" ? `Contactar ${name}` : `Contact ${name}`;

  return (
    <div
      data-testid="contact-popup"
      role="dialog"
      aria-modal="true"
      style={{ position: "fixed", inset: 0, zIndex: 220, display: "flex", alignItems: "flex-end" }}
    >
      {/* Scrim */}
      <button
        type="button"
        aria-label={t("dialog_close") || "Close"}
        onClick={onClose}
        data-testid="contact-popup-scrim"
        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", border: "none", padding: 0 }}
      />

      {/* Sheet */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 480,
          margin: "0 auto",
          background: "#171717",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          borderTop: "1px solid rgba(232,184,75,0.3)",
          padding: "10px 20px 24px",
          boxShadow: "0 -8px 32px rgba(0,0,0,0.5)",
        }}
      >
        {/* Handle bar */}
        <div
          aria-hidden="true"
          style={{ width: 40, height: 4, background: "rgba(232,184,75,0.45)", borderRadius: 9999, margin: "4px auto 8px" }}
        />

        {/* Close button — 44px touch target */}
        <button
          type="button"
          onClick={onClose}
          data-testid="contact-popup-close"
          aria-label={t("dialog_close") || "Close"}
          className="absolute grid place-items-center hover:opacity-80 transition-opacity"
          style={{
            top: 6,
            right: 6,
            width: 44,
            height: 44,
            minWidth: 44,
            minHeight: 44,
            background: "transparent",
            border: "none",
            padding: 0,
          }}
        >
          <span
            aria-hidden="true"
            className="grid place-items-center"
            style={{
              width: 32,
              height: 32,
              borderRadius: 9999,
              background: "rgba(20,20,20,0.8)",
              border: "0.5px solid #333",
              color: "#fff",
            }}
          >
            <X style={{ width: 16, height: 16 }} strokeWidth={1.5} />
          </span>
        </button>

        {/* Title */}
        <h2
          data-testid="contact-popup-title"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 20,
            color: "#F5F1E8",
            lineHeight: 1.25,
            marginRight: 48,
            marginBottom: 16,
          }}
        >
          {title}
        </h2>

        {/* Editable pre-filled message card */}
        <div
          style={{
            background: "#1a1a1a",
            borderRadius: 10,
            padding: 12,
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <label
            htmlFor="contact-popup-message"
            style={{
              display: "block",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 11,
              color: "#E8B84B",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            {lang === "es" ? "Mensaje" : "Message"}
          </label>
          <textarea
            id="contact-popup-message"
            data-testid="contact-popup-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            style={{
              width: "100%",
              background: "transparent",
              border: "none",
              outline: "none",
              resize: "vertical",
              color: "#F5F1E8",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              lineHeight: 1.45,
              padding: 0,
              minHeight: 64,
            }}
          />
        </div>

        {/* Buttons row */}
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button
            type="button"
            data-testid="contact-popup-send"
            onClick={sendMessage}
            disabled={!canSend}
            className="inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-95"
            style={{
              flex: 1,
              height: 48,
              minHeight: 48,
              background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)",
              color: "#0B0B0B",
              border: "none",
              borderRadius: 12,
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              padding: "0 12px",
              opacity: canSend ? 1 : 0.5,
            }}
          >
            <WaIcon size={18} />
            <span>{lang === "es" ? "Enviar Mensaje" : "Send Message"}</span>
          </button>

          {hasWhatsapp && (
            <button
              type="button"
              data-testid="contact-popup-call"
              onClick={callViaWhatsapp}
              className="inline-flex items-center justify-center gap-2 transition-colors hover:bg-[rgba(232,184,75,0.06)]"
              style={{
                flex: 1,
                height: 48,
                minHeight: 48,
                background: "transparent",
                color: "#E8B84B",
                border: "1.5px solid rgba(232,184,75,0.55)",
                borderRadius: 12,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 16,
                fontWeight: 600,
                padding: "0 12px",
              }}
            >
              <Phone style={{ width: 18, height: 18 }} strokeWidth={2} />
              <span>{lang === "es" ? "Llamar" : "Call"}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function WaIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24z" />
    </svg>
  );
}
